import { DateTime } from 'luxon'
import { ImportReportDTO } from '@invivodf/common/src/contexts/discount/infra/datasource/ImportReportDTO'
import { ImportReport } from '@invivodf/common/src/contexts/parameters'
import { createWishlistSdk, WishlistSDK } from '@invivodf/wishlist-sdk'
import { ProductSDK } from '@b2ag/module-product-sdk'
import { useAuthService } from '@b2ag/auth'
import { buildCallerId } from '@invivodf/front-seller-caller-id'
import { ImportReportSummary } from '@invivodf/common/src/contexts/parameters/domain/ImportReport'
import { createAxiosClientWithAladinBearer } from '@/utils/axios'
import { translateTypeFields } from '@/services/import-reports/translate-type.fields'
import {
  normalizeRecommendedListsReport,
  normalizeRecommendedListsReportSummary,
} from './recommended-lists/import-report.mapper'
import { mapImportReportContent } from './import-report.mapper'

function byUpdatedDate(firstReport, secondReport) {
  return DateTime.fromISO(secondReport.imported_at).diff(DateTime.fromISO(firstReport.imported_at)).milliseconds
}

export const normalizeReportSummary = (report): ImportReportSummary => ({
  ...report,
  imported_at: report.updated_at || report.created_at,
  translated_type: translateTypeFields(report.type),
})

export const normalizeReport = (report): ImportReport => ({
  ...normalizeReportSummary(report),
  report: mapImportReportContent(report.report),
})

export interface HttpClient {
  get<T>(path: string): Promise<{ status: number; data: T }>
}

export interface ImportReportFetcher {
  fetchOne(cooperativeId: number, id: number): Promise<ImportReport>
  fetchAll: (cooperativeId: number) => Promise<ImportReportSummary[]>
  types?: string[]
}

export default class ImportReportsService {
  private readonly httpClient: HttpClient

  private readonly wishlistSdk: WishlistSDK

  private fetchers: ImportReportFetcher[]

  constructor(
    private readonly cooperativeApiHost,
    private readonly productApiHost,
    private readonly orderApiHost,
    private readonly discountApiHost,
    private readonly wishlistApiHost,
    private readonly productSdk: ProductSDK,
  ) {
    // @ts-ignore EXPLIQUEZ POURQUOI VOUS IGNOREZ CETTE ERREUR
    this.httpClient = createAxiosClientWithAladinBearer()
    this.wishlistSdk = createWishlistSdk(this.wishlistApiHost, {
      accessTokenSource: () => useAuthService().getAccessToken(),
      callerId: buildCallerId(),
    })
    this.fetchers = [
      this.getProductCooperativeVariantReports(),
      this.getVariantSegmentationReports(),
      this.getAllPartnerReports(),
      this.getOffersReports(),
      this.getStockReports(),
      this.getQuotaReports(),
      this.getDiscountAssociationsReports(),
      this.getRecommendedListsReports(),
    ]
  }

  private getDiscountAssociationsReports(): ImportReportFetcher {
    return {
      fetchAll: async (cooperativeId: number) => {
        const url = `${this.discountApiHost}/v2/discount-associations/import-reports?seller_id=${cooperativeId}`

        return this.fetchAndIgnoreError(url)
      },
      fetchOne: async (cooperativeId: number, importReportId: number) => {
        const url = `${this.discountApiHost}/v2/discount-associations/import-reports/${importReportId}?seller_id=${cooperativeId}`
        const response = await this.httpClient.get<ImportReportDTO>(url)

        return normalizeReport(response.data)
      },
      types: ['discount-association'],
    }
  }

  private getQuotaReports(): ImportReportFetcher {
    return {
      fetchAll: async (cooperativeId: number) => {
        const url = `${this.productApiHost}/v1/cooperatives/${cooperativeId}/quotas/import-reports`

        return this.fetchAndIgnoreError(url)
      },
      fetchOne: async (cooperativeId: number, importReportId: number) => {
        const url = `${this.productApiHost}/v1/cooperatives/${cooperativeId}/quotas/import-reports/${importReportId}`
        const response = await this.httpClient.get<ImportReportDTO>(url)

        return normalizeReport(response.data)
      },
      types: ['quota'],
    }
  }

  private getStockReports(): ImportReportFetcher {
    return {
      fetchAll: async (cooperativeId: number) => {
        const url = `${this.orderApiHost}/v1/cooperatives/${cooperativeId}/stocks/import-reports`

        return this.fetchAndIgnoreError(url)
      },
      fetchOne: async (cooperativeId: number, importReportId: number) => {
        const url = `${this.orderApiHost}/v1/cooperatives/${cooperativeId}/stocks/import-reports/${importReportId}`
        const response = await this.httpClient.get<ImportReportDTO>(url)

        return normalizeReport(response.data)
      },
      types: ['stocks'],
    }
  }

  private getOffersReports(): ImportReportFetcher {
    return {
      fetchAll: async (cooperativeId: number) => {
        const url = `${this.productApiHost}/v1/cooperatives/${cooperativeId}/offers/import-reports`

        return this.fetchAndIgnoreError(url)
      },
      fetchOne: async (cooperativeId: number, importReportId: number) => {
        const url = `${this.productApiHost}/v1/cooperatives/${cooperativeId}/offers/import-reports/${importReportId}`
        const response = await this.httpClient.get<ImportReportDTO>(url)

        return normalizeReport(response.data)
      },
      types: ['offer', 'offer_delta', 'bulk_delete_offer'],
    }
  }

  private getProductCooperativeVariantReports(): ImportReportFetcher {
    return {
      fetchAll: async (cooperativeId: number) =>
        this.fetchReportsAndIgnoreError({ distributorId: cooperativeId, reportType: 'cooperative-variants' }),
      fetchOne: async (cooperativeId: number, importReportId: number) => {
        const report = await this.productSdk.getDistributorImportReport({
          distributorId: String(cooperativeId),
          reportType: 'cooperative-variants',
          importReportId: String(importReportId),
        } as any)

        // Le SDK avale le 422 et renvoie null : on rejette, comme le faisait axios avant.
        if (!report) {
          throw new Error(`Rapport d'import ${importReportId} introuvable pour le distributeur ${cooperativeId}`)
        }

        return normalizeReport(report)
      },
      types: ['variant'],
    }
  }

  private getVariantSegmentationReports(): ImportReportFetcher {
    return {
      fetchAll: async (cooperativeId: number) => {
        const url = `${this.productApiHost}/v1/cooperatives/${cooperativeId}/variant-segmentations/import-reports`

        return this.fetchAndIgnoreError(url)
      },
      fetchOne: async (cooperativeId: number, importReportId: number) => {
        const url = `${this.productApiHost}/v1/cooperatives/${cooperativeId}/variant-segmentations/import-reports/${importReportId}`
        const response = await this.httpClient.get<ImportReportDTO>(url)

        return normalizeReport(response.data)
      },
      types: ['variant-segment'],
    }
  }

  private getRecommendedListsReports(): ImportReportFetcher {
    return {
      fetchAll: async (cooperativeId: number) => {
        const result = await this.wishlistSdk.searchRecommendedListsImportReports({
          page: 1,
          limit: 10,
          distributorId: cooperativeId.toString(),
        })

        if (!result.isSuccess()) {
          return []
        }

        return result.data.recommendedListsImportReports.map(normalizeRecommendedListsReportSummary)
      },
      fetchOne: async (cooperativeId: number, importReportId: number) => {
        const result = await this.wishlistSdk.getRecommendedListImportReport({ reportId: importReportId.toString() })

        if (!result.isSuccess()) {
          throw result.data
        }

        return normalizeRecommendedListsReport(result.data)
      },
      types: ['recommended_lists'],
    }
  }

  private getAllPartnerReports(): ImportReportFetcher {
    return {
      fetchAll: async (cooperativeId: number) => {
        const url = `${this.cooperativeApiHost}/v1/partners/${cooperativeId}/import-reports`

        return this.fetchAndIgnoreError(url)
      },
      fetchOne: async (cooperativeId: number, importReportId: number) => {
        const url = `${this.cooperativeApiHost}/v1/partners/${cooperativeId}/import-reports/${importReportId}`
        const response = await this.httpClient.get<ImportReportDTO>(url)

        return normalizeReport(response.data)
      },
    }
  }

  async listAll(cooperativeId: number) {
    const importReports = await Promise.all(this.fetchers.map((fetcher) => fetcher.fetchAll(cooperativeId)))

    return importReports.flat().sort(byUpdatedDate)
  }

  async fetchAndIgnoreError(baseUrl: string): Promise<ImportReportSummary[]> {
    try {
      const { status, data } = await this.httpClient.get<ImportReportDTO[]>(baseUrl)
      return status === 204 ? [] : data.map(normalizeReportSummary)
    } catch (error) {
      return []
    }
  }

  async get(cooperativeId, importType, importReportId) {
    const fetcher = this.fetchers.find((item) => item.types?.includes(importType))

    if (fetcher) {
      return fetcher.fetchOne(cooperativeId, importReportId)
    }

    const response = await this.httpClient.get<ImportReportDTO>(
      `${this.cooperativeApiHost}/v1/partners/${cooperativeId}/import-reports/${importReportId}`,
    )
    return normalizeReport(response.data)
  }

  async getLastProductImport(cooperativeId, importType) {
    return this.fetchLatestReportAndIgnoreError(cooperativeId, importType)
  }

  async getLastZoneChargesImportReport(cooperativeId) {
    return this.fetchLatestReportAndIgnoreError(cooperativeId, 'zone-charges')
  }

  private async fetchLatestReportAndIgnoreError(cooperativeId, reportType: string) {
    try {
      const report = await this.productSdk.getDistributorLatestImportReport({
        distributorId: String(cooperativeId),
        reportTypeLatest: reportType,
      } as any)

      if (!report) return { data: {} }

      return normalizeReport(report)
    } catch (error) {
      return Promise.resolve({ data: {} })
    }
  }

  private async fetchReportsAndIgnoreError(params: {
    distributorId: number
    reportType: string
  }): Promise<ImportReportSummary[]> {
    try {
      const reports = await this.productSdk.getDistributorImportReports({
        distributorId: String(params.distributorId),
        reportType: params.reportType,
      } as any)

      return (reports ?? []).map(normalizeReportSummary)
    } catch (error) {
      return []
    }
  }
}
